import { type FunctionComponent } from 'react';
import {
  AfterShipAdSlot,
  AfterShipAdsProvider,
  type AfterShipAdEventPayload,
  type AfterShipAdsPageType,
} from '@fff-web/aftership-ads';
import { track } from '@fff-web/fff-analytics';
import { aftershipAdsAppKey, aftershipAdsEnvironment } from '../../utils/env';
import { datadogLogger } from './datadogLogger';

type AfterShipAdProps = {
  /** `order_status_page` for /my-account/orders, `tracking_page` for shipment tracking. */
  pageType: AfterShipAdsPageType;
  /** LaunchDarkly `shipmentTrackingAds`, so ads can be killed without killing the page. */
  isEnabled: boolean;
  orderId?: string;
  slotName?: string;
  className?: string;
};

const trackAdEvent = (trackName: string) => (payload: AfterShipAdEventPayload) => {
  track({
    trackName,
    properties: {
      ad_provider: 'aftership',
      ad_slot: payload.slotName,
      page_type: payload.pageType,
      ...(payload.orderId ? { order_id: payload.orderId } : {}),
    },
  });
};

const handleImpression = trackAdEvent('Ad Impression');
const handleClick = trackAdEvent('Ad Clicked');

/**
 * AfterShip ad slot, wired to this app's key, consent gate, logger and analytics.
 *
 * Renders nothing — no placeholder, no reserved height — when the flag is off, targeting
 * consent (OneTrust C0004, via `@fff-web/consent`) is missing, the SDK fails to load, or
 * AfterShip returns no ad.
 *
 * NOTE: the payload sent to AfterShip is deliberately limited to `page_type` and
 * `order_id`. No member identity, address, order value or line items are shared pending
 * contract/legal sign-off.
 */
export const AfterShipAd: FunctionComponent<AfterShipAdProps> = ({
  pageType,
  isEnabled,
  orderId,
  slotName,
  className,
}) => (
  <AfterShipAdsProvider
    appKey={aftershipAdsAppKey}
    environment={aftershipAdsEnvironment}
    isEnabled={isEnabled}
    logger={datadogLogger}
    onImpression={handleImpression}
    onAdClick={handleClick}
  >
    <AfterShipAdSlot
      attributes={{ page_type: pageType, ...(orderId ? { order_id: orderId } : {}) }}
      slotName={slotName}
      className={className}
    />
  </AfterShipAdsProvider>
);
